import { useQuery } from "@tanstack/react-query";
import type { FirstPlan, ProfileDetail } from "@amber/profiles/contracts";
import { AppLink, Button, Tag, Overline } from "@amber/ui";
import { sourcesQuery } from "../queries";
import { HealthLabel } from "../sources/SourceVisuals";

export function ProfileSourcesTab({
  profile,
  plan,
  onReview,
}: {
  profile: ProfileDetail;
  plan: FirstPlan | null;
  onReview: () => void;
}) {
  const sources = useQuery(sourcesQuery({ person: profile.id })).data?.sources ?? [];
  const collected = sources.filter((s) => s.platform.collected);
  const known = sources.filter((s) => !s.platform.collected);
  const open = [...(plan?.preselected ?? []), ...(plan?.suggestions ?? [])];
  return (
    <div className="overflow-auto px-4 pb-6 text-ui">
      <Overline className="m-0 mt-1">Sources</Overline>
      {collected.length === 0 ? (
        <p className="m-0 mt-1 text-md text-muted">No approved sources yet.</p>
      ) : (
        <ul className="m-0 mt-1.5 list-none p-0">
          {collected.map((s) => {
            const me = s.people.find((p) => p.id === profile.id);
            return (
              <li
                key={s.id}
                className="grid grid-cols-[minmax(0,1.4fr)_120px_minmax(0,1fr)_auto] items-center gap-3 border-t border-divider py-2"
              >
                <AppLink href={`/sources/${s.id}`} className="truncate font-strong">
                  {s.title}
                </AppLink>
                <span className="text-md text-muted">
                  {s.platform.label}
                  {me?.relationship ? ` · ${me.relationship}` : ""}
                </span>
                <HealthLabel state={s.health.state} label={s.health.label} />
                <span className="flex gap-1">
                  {me?.required ? <Tag tone="strong">Required</Tag> : null}
                  {me?.in_plan ? <Tag>{s.tier_label}</Tag> : null}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <Overline className="m-0 mt-5">Known accounts we don't collect</Overline>
      {known.length === 0 ? (
        <p className="m-0 mt-1 text-md text-muted">None recorded.</p>
      ) : (
        <ul className="m-0 mt-1.5 list-none p-0">
          {known.map((s) => (
            <li
              key={s.id}
              className="grid grid-cols-[minmax(0,1.4fr)_120px_auto] items-center gap-3 border-t border-divider py-2"
            >
              <a href={s.url} target="_blank" rel="noreferrer" className="truncate">
                {s.title}
              </a>
              <span className="text-md text-muted">{s.platform.label}</span>
              <Tag tone="quiet">Not collected yet</Tag>
            </li>
          ))}
        </ul>
      )}
      <Overline className="m-0 mt-5">Suggested sources</Overline>
      {open.length === 0 ? (
        <p className="m-0 mt-1 text-md text-muted">No suggestions are waiting for a decision.</p>
      ) : (
        <div className="mt-1.5 flex items-center gap-3">
          <span className="text-md">
            {open.length} suggestion{open.length === 1 ? "" : "s"} awaiting a decision.
          </span>
          <Button size="sm" onClick={onReview}>
            Review
          </Button>
        </div>
      )}
      <p className="m-0 mt-4 text-md text-muted">
        Download problems don't change a source's health; shared sources are scanned once for everyone.
      </p>
    </div>
  );
}
